import { ISobCompetitor } from 'partner360/models/ISobCompetitor'

import { HP_COMPETITOR_ID } from '../constants'

import { calculateWeightAverage } from './calculateWeightAverage'
import { getAllFoundDescendants } from './getAllFoundDescendants'
import { getCalckedSellThruFields } from './getCalckedSellThruFields'
import { getChangedItemWithUpdatedSellThruAndYoYFields } from './getChangedItemWithUpdatedSellThruAndYoYFields'
import { getNdpUsdField } from './getNdpUsdField'
import { getSellThruField } from './getSellThruField'
import { isSobField } from './isSobField'

interface IGetUpdatedParentData {
  competitors: ISobCompetitor[]
  competitorId: string
  entryId: string
  field: string
  observedFields: string[]
}

interface ICalculateAverageWeight {
  allDescendants: ISobCompetitor[]
  observedFields: string[]
  hpItem?: ISobCompetitor
  item: ISobCompetitor
}

const calculateAverageWeight = ({
  allDescendants,
  observedFields,
  item,
  hpItem,
}: ICalculateAverageWeight): Record<string, number | null> => {
  return observedFields.reduce(
    (acc, field) => {
      if (isSobField(field)) {
        const sellThruNpdUsdField = getNdpUsdField(getSellThruField(field))
        const sobValues = allDescendants.map((el) => el[field])
        const sellThruUsdNdpValues = allDescendants.map((el) => el[sellThruNpdUsdField])

        const weightAverage = calculateWeightAverage({ sobValues, ndpValues: sellThruUsdNdpValues })
        const calckedSellThru = getCalckedSellThruFields({
          field,
          value: weightAverage,
          hpItem,
          item,
        })
        return {
          ...acc,
          ...calckedSellThru,
          [field]: weightAverage,
        }
      }
      return acc
    },
    {} as Record<string, number | null>
  )
}

const recalculateParentData = ({
  competitors,
  competitorId,
  field,
  entryId,
  observedFields,
}: IGetUpdatedParentData): ISobCompetitor[] => {
  const item = competitors.find((el) => el.CompetitorID === competitorId && el.EntryId === entryId)
  const hpItem = competitors.find(
    (el) => el.CompetitorID === HP_COMPETITOR_ID && el.EntryId === entryId
  )

  if (!item || !item.ParentId) {
    return competitors
  }

  const parentIndex = competitors.findIndex(
    (el) => el.EntryId === item.ParentId && el.CompetitorID === competitorId
  )
  if (parentIndex === -1) {
    return competitors
  }

  const parent = competitors[parentIndex]

  const allDescendants = getAllFoundDescendants({
    competitors,
    parentId: parent.EntryId,
    competitorId: competitorId,
  })

  const sums = calculateAverageWeight({ allDescendants, observedFields, item, hpItem })
  const newParent = { ...parent, ...sums }
  const updatedItem = observedFields.reduce((acc, field) => {
    if (isSobField(field)) {
      const updatedItem = getChangedItemWithUpdatedSellThruAndYoYFields({
        field,
        value: acc[field],
        item: newParent,
        hpItem,
      })
      return { ...acc, ...updatedItem }
    }
    return acc
  }, newParent)

  const newData = [
    ...competitors.slice(0, parentIndex),
    updatedItem,
    ...competitors.slice(parentIndex + 1),
  ]

  return recalculateParentData({
    competitors: newData,
    competitorId: newParent.CompetitorID,
    entryId: newParent.EntryId,
    field: field,
    observedFields,
  })
}
export const getUpdatedParentData = ({
  competitors,
  competitorId,
  field,
  entryId,
  observedFields,
}: IGetUpdatedParentData) => {
  return recalculateParentData({
    competitors,
    competitorId,
    entryId,
    field,
    observedFields,
  })
}
